import { createClient } from '@supabase/supabase-js'
import type { User } from '@supabase/supabase-js'
import type { Tables } from '../types/supabase'
import { Database, TeamRole } from '../types/supabase'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing required environment variables: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY')
}

// Create default client with anon key - now with Auth support
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
})

// Legacy team authentication functions (will be deprecated once migration is complete)
export const authenticateTeam = async (teamId: string) => {
  const isUUID = teamId.includes('-')
  const requestBody = isUUID 
    ? { team_id: teamId }
    : { slack_team_id: teamId }
  
  // console.log('Authenticating team:', { teamId, isUUID, requestBody })
  
  const response = await fetch(`${supabaseUrl}/functions/v1/dashboard-auth`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey': supabaseAnonKey,
    },
    body: JSON.stringify(requestBody)
  })
  
  if (!response.ok) {
    const errorText = await response.text()
    console.error('Authentication failed:', response.status, errorText)
    throw new Error('Authentication failed')
  }
  
  return response.json()
}

export const getAvailableTeams = async () => {
  const response = await fetch(`${supabaseUrl}/functions/v1/dashboard-auth`, {
    method: 'GET',
    headers: {
      'apikey': supabaseAnonKey,
    }
  })
  
  if (!response.ok) {
    throw new Error('Failed to fetch teams')
  }
  
  return response.json()
}

// ============================================================================
// SLACK-ONLY AUTHENTICATION FUNCTIONS
// ============================================================================

// Slack Authentication - Using Supabase's native Slack OIDC provider
export const signInWithSlack = async (redirectTo?: string) => {
  // Determine the correct redirect URL
  const currentUrl = window.location.origin
  const isLocalhost = currentUrl.includes('localhost')
  const baseUrl = isLocalhost ? 'http://localhost:3000' : 'https://app.syncsimple.ai'
  const defaultRedirect = `${baseUrl}/dashboard`
  
  // console.log('signInWithSlack: Using Supabase native Slack OIDC')
  // console.log('signInWithSlack: Redirect URL:', redirectTo || defaultRedirect)
  
  // Use Supabase's native Slack OIDC authentication
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'slack_oidc',
    options: {
      redirectTo: redirectTo || defaultRedirect
    }
  })
  
  if (error) {
    console.error('Slack OIDC authentication error:', error)
    throw error
  }
  
  return data
}

// Setup user and team after Slack authentication - simplified for OIDC
export const setupSlackUserAndTeam = async (plan?: string) => {
  const user = await getCurrentUser()
  if (!user) {
    throw new Error('No authenticated user found')
  }

  console.log('Setting up Slack user and team for:', user.email)
  console.log('User metadata:', user.user_metadata)
  console.log('App metadata:', user.app_metadata)
  console.log('Custom claims:', user.user_metadata?.custom_claims)

  // Extract Slack data from OIDC claims
  const slackTeamId = user.user_metadata?.custom_claims?.['https://slack.com/team_id'] || 
                      user.user_metadata?.['https://slack.com/team_id']
  const slackUserId = user.user_metadata?.custom_claims?.['https://slack.com/user_id'] || 
                      user.user_metadata?.['https://slack.com/user_id'] ||
                      user.user_metadata?.sub ||
                      user.user_metadata?.provider_id
  const slackUserName = user.user_metadata?.custom_claims?.['https://slack.com/user_name'] || 
                        user.user_metadata?.['https://slack.com/user_name'] ||
                        user.user_metadata?.name ||
                        user.user_metadata?.full_name ||
                        user.email?.split('@')[0] || 'Unknown'
  
  // For team name, we need to find it from the database since OIDC doesn't provide it
  // We'll look up the team by slack_team_id and use that name, or fall back to a default
  let slackTeamName = `${slackUserName}'s Workspace` // Default fallback

  console.log('Extracted Slack data:', { slackTeamId, slackTeamName, slackUserId, slackUserName })

  if (!slackTeamId) {
    console.error('No Slack team ID found in user metadata')
    throw new Error('No Slack team ID found in user metadata')
  }

  if (!slackUserId) {
    console.error('No Slack user ID found in user metadata')
    throw new Error('No Slack user ID found in user metadata')
  }

  try {
    // First, try to find existing team to get the real team name
    const { data: existingTeam } = await supabase
      .from('teams')
      .select('name')
      .eq('slack_team_id', slackTeamId)
      .single()
    
    if (existingTeam?.name) {
      slackTeamName = existingTeam.name
      console.log('Found existing team name:', slackTeamName)
    }

    // Find or create the team
    console.log('Finding/creating team for Slack workspace:', { slackTeamId, teamName: slackTeamName, plan })
    
    const { data: teamResult, error: teamFunctionError } = await supabase.rpc(
      'find_or_create_slack_team',
      {
        p_slack_team_id: slackTeamId,
        p_team_name: slackTeamName,
        p_auth_user_id: user.id,
        p_plan: plan || 'trial' // Default to trial if no plan specified
      } as any
    )

    console.log('RPC result:', { teamResult, teamFunctionError })

    if (teamFunctionError) {
      console.error('Error in find_or_create_slack_team:', teamFunctionError)
      throw teamFunctionError
    }

    if (!teamResult) {
      console.error('No team returned from find_or_create_slack_team')
      throw new Error('Failed to create or find team')
    }

    // The RPC function returns the result nested under the function name
    // Handle both possible response structures
    let functionResult: any
    if (typeof teamResult === 'object' && 'find_or_create_slack_team' in teamResult) {
      functionResult = (teamResult as any).find_or_create_slack_team
    } else {
      functionResult = teamResult
    }
    
    console.log('Function result:', functionResult)

    // Now extract the team data from the result
    const teamData = functionResult.team
    const isNewTeam = functionResult.is_new_team
    const userRole = functionResult.user_role

    if (!teamData || !teamData.id) {
      console.error('No team data in result:', functionResult)
      throw new Error('Invalid response from team creation function')
    }

    console.log('Team created/found:', { teamId: teamData.id, isNewTeam, userRole })

    // Create or update the user record using the database function
    console.log('Creating/updating user record using link_slack_user_to_auth function...')
    
    const { data: linkResult, error: linkError } = await (supabase as any)
      .rpc('link_slack_user_to_auth', {
        p_auth_user_id: user.id,
        p_slack_user_id: slackUserId,
        p_team_id: teamData.id,
        p_user_name: slackUserName,
        p_user_email: user.email
      }) as { data: { success: boolean; user_id: string; action: string } | null; error: any }
    
    if (linkError) {
      console.error('Error linking user:', linkError)
      throw linkError
    }
    
    console.log('User link result:', linkResult)
    
    if (!linkResult?.success || !linkResult?.user_id) {
      console.error('Failed to link user:', linkResult)
      throw new Error('Failed to link Slack user to auth user')
    }
    
    // Get the user data
    const { data: userData, error: userFetchError } = await supabase
      .from('users')
      .select()
      .eq('id', linkResult.user_id)
      .single()
    
    if (userFetchError || !userData) {
      console.error('Error fetching linked user:', userFetchError)
      throw new Error('Failed to fetch user after linking')
    }

    console.log('User record created/updated:', userData)

    if (!userData || !userData.id) {
      console.error('Failed to create or retrieve user data')
      throw new Error('Failed to create or retrieve user data')
    }

    // Create team membership if it doesn't exist
    console.log('Creating/updating team membership...')
    
    // Check if membership already exists
    const { data: existingMembership } = await supabase
      .from('team_members')
      .select()
      .eq('team_id', teamData.id)
      .eq('auth_user_id', user.id)
      .single()

    if (!existingMembership) {
      const { error: memberError } = await supabase
        .from('team_members')
        .insert({
          team_id: teamData.id,
          auth_user_id: user.id,
          user_id: userData.id,
          role: userRole,
          joined_at: new Date().toISOString()
        })

      if (memberError) {
        console.error('Error creating team membership:', memberError)
        throw memberError
      }
      console.log('Team membership created successfully')
    } else {
      console.log('Team membership already exists')
    }

    console.log('Slack user and team setup completed successfully')
    return { user: userData, team: teamData }
  } catch (error) {
    console.error('Error in setupSlackUserAndTeam:', error)
    throw error
  }
}

export const signOut = async () => {
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}

export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser()
  if (error) throw error
  return user
}

export const getCurrentSession = async () => {
  // console.log('getCurrentSession: Starting...')
  try {
    // console.log('getCurrentSession: About to call getSession...')
    const { data: { session }, error } = await supabase.auth.getSession()
    
    // console.log('getCurrentSession: Got response:', { session: session ? 'exists' : 'null', error })
    if (error) throw error
    return session
  } catch (error) {
    console.error('getCurrentSession: Error:', error)
    throw error
  }
}

// Team Management Functions
export const getUserTeams = async (user?: User): Promise<Tables<'team_members'>[]> => {
  const currentUser = user || await getCurrentUser()
  if (!currentUser) {
    throw new Error('No authenticated user found')
  }

  const { data, error } = await supabase
    .from('team_members')
    .select(`
      *,
      teams:teams(*)
    `)
    .eq('auth_user_id', currentUser.id)
    .order('joined_at', { ascending: false })

  if (error) {
    console.error('Error fetching user teams:', error)
    throw error
  }

  return data || []
}

export const getTeamMembers = async (teamId: string) => {
  // console.log('getTeamMembers: Starting query for team:', teamId)
  
  // Get all team members for this team with user details using the view
  // RLS policies ensure users can only see teams they're members of
  const { data: allMembers, error } = await supabase
    .from('team_members_with_users')
    .select('*')
    .eq('team_id', teamId)
    .order('joined_at', { ascending: true })
  
  if (error) {
    console.error('getTeamMembers: Error:', error)
    throw error
  }
  
  // console.log('getTeamMembers: Got members:', allMembers)
  
  // Transform the data to match the expected format
  const transformedMembers = allMembers?.map(member => ({
    ...member,
    users: {
      name: member.user_name,
      email: member.user_email
    }
  }))
  
  return transformedMembers
}

export const getUserRole = async (teamId: string) => {
  const user = await getCurrentUser()
  if (!user) return null
  
  const { data, error } = await supabase
    .from('team_members')
    .select('role')
    .eq('team_id', teamId)
    .eq('auth_user_id', user.id)
    .single()
  
  if (error) throw error
  return data?.role as TeamRole
}

export const checkUserTeamAccess = async (teamId: string): Promise<{ hasAccess: boolean, role?: TeamRole }> => {
  const user = await getCurrentUser()
  if (!user) return { hasAccess: false }
  
  const { data, error } = await supabase
    .from('team_members')
    .select('role')
    .eq('team_id', teamId)
    .eq('auth_user_id', user.id)
    .single()
  
  if (error || !data) return { hasAccess: false }
  return { hasAccess: true, role: data.role as TeamRole }
}

// Team Invitation Functions
export const inviteUserToTeam = async (teamId: string, email: string, role: TeamRole = 'member') => {
  const user = await getCurrentUser()
  if (!user) throw new Error('User not authenticated')
  
  const session = await getCurrentSession()
  if (!session) throw new Error('No session found')
  
  // First create the invitation record
  const { data, error } = await supabase
    .from('team_invitations')
    .insert({
      team_id: teamId,
      email,
      role,
      invited_by: user.id
    })
    .select()
    .single()
  
  if (error) throw error
  
  // Then send the invitation email
  const { error: emailError } = await supabase.functions.invoke('send-team-invitation', {
    body: {
      teamId,
      email,
      role,
      invitedBy: user.id
    },
    headers: {
      Authorization: `Bearer ${session.access_token}`
    }
  })
  
  if (emailError) {
    console.error('Failed to send invitation email:', emailError)
    // Don't throw - the invitation was created, just the email failed
  }
  
  // Update Stripe subscription quantity if team has an active subscription
  try {
    await supabase.functions.invoke('update-subscription-quantity', {
      body: { teamId }
    })
  } catch (error) {
    console.error('Failed to update subscription quantity:', error)
    // Don't throw - this is not critical to the invitation flow
  }
  
  return data
}

export const acceptTeamInvitation = async (token: string) => {
  const { data, error } = await supabase.rpc('accept_team_invitation', {
    invitation_token: token
  })
  
  if (error) throw error
  return data
}

export const getTeamInvitations = async (teamId: string) => {
  const { data, error } = await supabase
    .from('team_invitations')
    .select('*')
    .eq('team_id', teamId)
    .is('accepted_at', null)
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false })
  
  if (error) throw error
  return data
}

export const getUserInvitations = async () => {
  const user = await getCurrentUser()
  if (!user?.email) {
    // console.log('getUserInvitations: No user email found')
    return []
  }
  
  // console.log('getUserInvitations: Getting invitations for:', user.email)
  
  // Get user's invitations
  const { data: invitations, error: invitationError } = await supabase
    .from('team_invitations')
    .select('*')
    .eq('email', user.email)
    .is('accepted_at', null)
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false })
  
  if (invitationError) {
    console.error('getUserInvitations: Error fetching invitations:', invitationError)
    throw invitationError
  }
  
  // console.log('getUserInvitations: Got invitations:', invitations)
  
  if (!invitations || invitations.length === 0) {
    return []
  }
  
  // Get team names for the invitations
  const teamIds = invitations.map(inv => inv.team_id)
  const { data: teams, error: teamsError } = await supabase
    .from('teams')
    .select('id, name')
    .in('id', teamIds)
  
  if (teamsError) {
    console.error('getUserInvitations: Error fetching team names:', teamsError)
    // Don't throw error, just return invitations without team names
    return invitations.map(inv => ({ ...inv, teams: null }))
  }
  
  // Combine data
  const result = invitations.map(invitation => ({
    ...invitation,
    teams: teams?.find(team => team.id === invitation.team_id) || null
  }))
  
  // console.log('getUserInvitations: Final result:', result)
  return result
}

// Team CRUD Functions  
export const createTeam = async (name: string, slackTeamId?: string) => {
  // console.log('createTeam: Starting...')
  
  const user = await getCurrentUser()
  // console.log('createTeam: Current user:', user)
  
  if (!user) throw new Error('User not authenticated')
  
  const session = await getCurrentSession()
  // console.log('createTeam: Current session:', session)
  
  // console.log('createTeam: About to insert team with data:', {
  //   name,
  //   slack_team_id: slackTeamId || `manual_${Date.now()}`
  // })
  
  const { data: team, error: teamError } = await supabase
    .from('teams')
    .insert({
      name,
      slack_team_id: slackTeamId || `manual_${Date.now()}`
    })
    .select()
    .single()
  
  // console.log('createTeam: Team insert result:', { team, teamError })
  if (teamError) {
    console.error('createTeam: Team creation failed:', teamError)
    throw teamError
  }
  
  // console.log('createTeam: About to add team membership for user:', user.id)
  
  // Find the user record in the users table
  const { data: userRecord, error: userError } = await supabase
    .from('users')
    .select('id')
    .eq('auth_user_id', user.id)
    .single()
  
  if (userError || !userRecord) {
    console.error('createTeam: User record not found:', userError)
    throw new Error('User record not found in users table')
  }
  
  // Add the creator as an admin
  const { error: memberError } = await supabase
    .from('team_members')
    .insert({
      team_id: team.id,
      auth_user_id: user.id,
      user_id: userRecord.id, // Add the user_id from the users table
      role: 'admin',
      joined_at: new Date().toISOString()
    })
  
  // console.log('createTeam: Team membership result:', { memberError })
  if (memberError) {
    console.error('createTeam: Team membership creation failed:', memberError)
    throw memberError
  }
  
  // console.log('createTeam: Successfully created team:', team)
  return team
}

export const updateTeamMemberRole = async (memberId: string, role: TeamRole) => {
  // console.log('updateTeamMemberRole: Starting update for member:', memberId, 'to role:', role)
  
  const { data, error } = await supabase
    .from('team_members')
    .update({ role })
    .eq('id', memberId)
    .select()
  
  // console.log('updateTeamMemberRole: Response:', { data, error })
  
  if (error) {
    console.error('updateTeamMemberRole: Error updating role:', error)
    throw error
  }
  
  // console.log('updateTeamMemberRole: Successfully updated role')
  return data
}

export const removeTeamMember = async (memberId: string) => {
  const { error } = await supabase
    .from('team_members')
    .delete()
    .eq('id', memberId)
  
  if (error) throw error
}

// ============================================================================
// EXISTING FUNCTIONS (Updated for new auth system)
// ============================================================================

export const getTeams = async () => {
  // console.log('getTeams: Making request to Supabase...')
  const { data, error } = await supabase
    .from('teams')
    .select('*')
    .order('created_at', { ascending: false })
  
  // console.log('getTeams: Response:', { data, error })
  if (error) throw error
  return data
}

// Check if team has bot tokens for slash commands
export const checkTeamBotTokens = async (teamId: string) => {
  const { data: team, error } = await supabase
    .from('teams')
    .select('slack_bot_token, slack_access_token, slack_team_id')
    .eq('id', teamId)
    .single()
  
  if (error) throw error
  
  return {
    hasBotToken: !!team?.slack_bot_token,
    hasAccessToken: !!team?.slack_access_token,
    slackTeamId: team?.slack_team_id
  }
}

// Generate Slack app installation URL
export const getSlackInstallUrl = async (slackTeamId?: string) => {
  const redirectUri = `${supabaseUrl}/functions/v1/slack-oauth`
  
  const params = new URLSearchParams({
    client_id: import.meta.env.VITE_SLACK_CLIENT_ID || '',
    scope: 'app_mentions:read,channels:history,channels:read,chat:write,chat:write.public,commands,files:read,groups:history,groups:read,im:history,im:read,im:write,mpim:read,team:read,users:read,users:read.email,users:write', // Bot scopes
    user_scope: '', // No user scopes needed
    redirect_uri: redirectUri,
  })
  
  // Add current user ID to state parameter if user is logged in
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (user) {
      params.append('state', `user_id=${user.id}`)
      // console.log('Added current user to OAuth state:', user.id)
    }
  } catch (error) {
    // console.log('No current user for OAuth state')
  }
  
  if (slackTeamId) {
    params.append('team', slackTeamId)
  }
  
  return `https://slack.com/oauth/v2/authorize?${params.toString()}`
}

export const getTeamStandups = async (teamId: string, date?: string) => {
  // console.log('getTeamStandups: Making request for team:', teamId)
  let query = supabase
    .from('standup_responses')
    .select(`
      *,
      users (
        name,
        slack_user_id
      )
    `)
    .eq('team_id', teamId)
    .order('created_at', { ascending: false })

  if (date) {
    query = query.eq('date', date)
  }

  const { data, error } = await query
  
  // console.log('getTeamStandups: Response:', { data, error })
  if (error) throw error
  return data
}

export const getStandupsByDateRange = async (
  teamId: string, 
  startDate: string, 
  endDate: string
) => {
  const { data, error } = await supabase
    .from('standup_responses')
    .select(`
      *,
      users (
        name,
        slack_user_id
      )
    `)
    .eq('team_id', teamId)
    .gte('date', startDate)
    .lte('date', endDate)
    .order('date', { ascending: false })
  
  if (error) throw error
  return data
}

export const getStandupsGroupedByDate = async (teamId: string, limit: number = 14) => {
  const { data, error } = await supabase
    .from('standup_responses')
    .select(`
      *,
      users (
        name,
        slack_user_id,
        timezone
      )
    `)
    .eq('team_id', teamId)
    .order('date', { ascending: false })
    .limit(limit * 10) // Get more entries to account for grouping
  
  if (error) throw error
  
  // Debug logging to see what's in the database
  // console.log('=== DATABASE STANDUP DATA DEBUG ===')
  data?.slice(0, 3).forEach((standup, i) => {
    // Parse date as local date to avoid timezone conversion
    const parsedDate = standup.date ? new Date(standup.date + 'T00:00:00') : null
    // console.log(`Standup ${i + 1}:`, {
    //   id: standup.id,
    //   rawDate: standup.date,
    //   rawCreatedAt: standup.created_at,
    //   parsedDate: parsedDate,
    //   parsedCreatedAt: standup.created_at ? new Date(standup.created_at) : null,
    //   localDateString: parsedDate ? parsedDate.getFullYear() + '-' + 
    //     String(parsedDate.getMonth() + 1).padStart(2, '0') + '-' + 
    //     String(parsedDate.getDate()).padStart(2, '0') : null
    // })
  })
  // console.log('=======================================')
  
  // Group by date
  const groupedStandups = data?.reduce((acc, standup) => {
    const date = standup.date
    if (!acc[date]) {
      acc[date] = []
    }
    acc[date].push(standup)
    return acc
  }, {} as Record<string, typeof data>)
  
  // Convert to array and sort by date
  const sortedGroups = Object.entries(groupedStandups || {})
    .sort(([dateA], [dateB]) => new Date(dateB).getTime() - new Date(dateA).getTime())
    .slice(0, limit)
  
  return sortedGroups
}

export const generateDailySummary = async (teamId: string, date: string) => {
  try {
    const { data: standups, error } = await supabase
      .from('standup_responses')
      .select(`
        *,
        users (
          name,
          slack_user_id
        )
      `)
      .eq('team_id', teamId)
      .eq('date', date)
    
    if (error) throw error
    if (!standups || standups.length === 0) {
      return null
    }
    
    // Extract all the standup content
    const standupContent = standups.map(standup => ({
      name: standup.users?.name || 'Unknown User',
      yesterday: (standup.questions as any)?.yesterday || '',
      today: (standup.questions as any)?.today || '',
      blockers: (standup.questions as any)?.blockers || '',
      sentiment: standup.sentiment || 0
    }))
    
    // Call edge function to generate summary
    const { data: summary, error: summaryError } = await supabase.functions.invoke('generate-daily-summary', {
      body: {
        standups: standupContent,
        date: date
      }
    })
    
    if (summaryError) {
      console.error('Error generating summary:', summaryError)
      return null
    }
    
    return summary
  } catch (error) {
    console.error('Error in generateDailySummary:', error)
    return null
  }
}

export const getUserTimezone = async (userId: string) => {
  const { data, error } = await supabase
    .from('users')
    .select('timezone')
    .eq('auth_user_id', userId)
    .single()
  
  if (error) throw error
  return data?.timezone || 'UTC'
}

export const updateUserTimezone = async (userId: string, timezone: string) => {
  const { data, error } = await supabase
    .from('users')
    .update({ timezone })
    .eq('auth_user_id', userId)
  
  if (error) throw error
  return data
}

export const uploadVoiceMemo = async (file: File, fileName: string) => {
  const { data, error } = await supabase.storage
    .from('voice-memos')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: false
    })

  if (error) throw error
  return data
}

export const getVoiceMemoUrl = async (path: string) => {
  const { data, error } = await supabase.storage
    .from('voice-memos')
    .createSignedUrl(path, 3600) // 1 hour expiry

  if (error) throw error
  return data.signedUrl
}

// Debug function to test authentication state
export const debugAuth = async () => {
  // console.log('=== DEBUG AUTH ===')
  // console.log('Supabase URL:', supabaseUrl)
  // console.log('Anon Key:', supabaseAnonKey?.substring(0, 50) + '...')
  
  try {
    const session = await getCurrentSession()
    // console.log('Current session:', session)
    
    if (session?.user) {
      // console.log('User ID:', session.user.id)
      // console.log('User email:', session.user.email)
      
      // Test direct query
      // console.log('Testing direct team_members query...')
      const { data, error } = await supabase
        .from('team_members')
        .select('*')
        .limit(5)
      
      // console.log('Direct query result:', { data, error })
    }
  } catch (error) {
    console.error('Debug auth error:', error)
  }
}

// Debug function to check team bot token status
export const debugTeamTokens = async (teamId: string) => {
  // console.log('=== DEBUG TEAM TOKENS ===')
  try {
    const { data: team, error } = await supabase
      .from('teams')
      .select('id, name, slack_team_id, slack_access_token, slack_bot_token')
      .eq('id', teamId)
      .single()
    
    if (error) {
      console.error('Error fetching team:', error)
      return
    }
    
    // console.log('Team data:', {
    //   id: team.id,
    //   name: team.name,
    //   slack_team_id: team.slack_team_id,
    //   hasAccessToken: !!team.slack_access_token,
    //   hasBotToken: !!team.slack_bot_token,
    //   accessTokenPreview: team.slack_access_token ? team.slack_access_token.substring(0, 10) + '...' : null,
    //   botTokenPreview: team.slack_bot_token ? team.slack_bot_token.substring(0, 10) + '...' : null
    // })
    
    return team
  } catch (error) {
    console.error('Debug team tokens error:', error)
  }
}

// Function to manually update bot token (for debugging)
export const updateTeamBotToken = async (teamId: string, botToken: string) => {
  // console.log('=== UPDATING TEAM BOT TOKEN ===')
  try {
    const { data, error } = await supabase
      .from('teams')
      .update({ slack_bot_token: botToken })
      .eq('id', teamId)
      .select()
      .single()
    
    if (error) {
      console.error('Error updating bot token:', error)
      return
    }
    
    // console.log('Bot token updated successfully:', {
    //   teamId: data.id,
    //   hasBotToken: !!data.slack_bot_token
    // })
    
    return data
  } catch (error) {
    console.error('Update bot token error:', error)
  }
}

// Add to window for debugging
if (typeof window !== 'undefined') {
  (window as any).debugAuth = debugAuth;
  (window as any).getUserTeams = getUserTeams;
  (window as any).supabase = supabase;
  (window as any).debugTeamTokens = debugTeamTokens;
  (window as any).updateTeamBotToken = updateTeamBotToken;
  (window as any).getSlackInstallUrl = getSlackInstallUrl;
  
  // Simple connectivity test
  (window as any).testConnection = async () => {
    // console.log('=== CONNECTION TEST ===')
    try {
      // Test basic REST API connectivity
      const response = await fetch(`${supabaseUrl}/rest/v1/`, {
        headers: {
          'apikey': supabaseAnonKey,
          'Content-Type': 'application/json'
        }
      })
      // console.log('REST API response:', response.status, response.statusText)
      
      // Test auth endpoint
      const authResponse = await fetch(`${supabaseUrl}/auth/v1/settings`, {
        headers: {
          'apikey': supabaseAnonKey,
          'Content-Type': 'application/json'
        }
      })
      // console.log('Auth API response:', authResponse.status, authResponse.statusText)
      
      // Test simple query without auth
      const { data, error } = await supabase
        .from('teams')
        .select('count')
        .limit(1)
      
      // console.log('Simple query result:', { data, error })
      
    } catch (error) {
      console.error('Connection test error:', error)
    }
  };
} 

// Import for team claiming
export const findUnclaimedTeams = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return []

    // Find teams where user has a Slack user record but no team membership
    const { data: unclaimedUsers, error } = await supabase
      .from('users')
      .select(`
        id,
        slack_user_id,
        name,
        team_id,
        teams:teams(id, name, slack_team_id)
      `)
      .is('auth_user_id', null)
    
    if (error) throw error

    // Filter to teams that don't already have this auth user as a member
    const { data: existingMemberships } = await supabase
      .from('team_members')
      .select('team_id')
      .eq('auth_user_id', user.id)
    
    const existingTeamIds = new Set(existingMemberships?.map(m => m.team_id) || [])
    
    return unclaimedUsers?.filter(u => 
      u.teams && u.team_id && !existingTeamIds.has(u.team_id)
    ) || []
    
  } catch (error) {
    console.error('Error finding unclaimed teams:', error)
    return []
  }
}

export const claimTeam = async (userRecordId: string, teamId: string) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')

    // Update the users record to link to auth account
    const { error: userError } = await supabase
      .from('users')
      .update({ auth_user_id: user.id })
      .eq('id', userRecordId)
    
    if (userError) throw userError

    // Create team membership
    const { error: membershipError } = await supabase
      .from('team_members')
      .insert({
        team_id: teamId,
        auth_user_id: user.id,
        user_id: userRecordId,
        role: 'admin', // Claiming user gets admin role
        joined_at: new Date().toISOString()
      })
    
    if (membershipError) throw membershipError

    return true
  } catch (error) {
    console.error('Error claiming team:', error)
    throw error
  }
} 